import React from 'react';
import { useDispatch } from 'react-redux';
import { Body7, Container, Select, TextField } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { DialogButtonRow } from '@ui/layout/Dialog';
import ErrorText from '@ui/ErrorText';
import { showSuccessBanner } from '@utils/application';
import { CATALOG_CODE_PATTERN, CATALOG_ITEM_STATUSES, createCatalogItem, updateCatalogItem } from '@services/fmdc';
import { SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { FieldGrid } from '@features/scoreSpec/styles';
import { FmdcTenantContext, useVocabularies, vocabularyOptions } from './FmdcTenantContext';
import { AliasEditor } from './AliasEditor';
import { CATALOG_STATUS_TOKENS } from './statusTokens';

// Create or edit a catalog item (admin). The create body and the item record spell two fields
// differently — `label` vs `canonical_item_label`, `sign_convention` vs
// `normalization_or_sign_convention` — so the form keeps its own draft and maps at the edges.
//
// A value kind can be a combination the vocabulary does not list (`currency_or_percent`), so the
// select carries an "Other…" option that reveals a text field, the ModelSelect shape.

export const FORM_MODES = { CREATE: 'create', EDIT: 'edit' };

const OTHER = '__other__';

const STATUS_SEGMENTS = [
  { key: CATALOG_ITEM_STATUSES.ACTIVE, label: 'Active', token: CATALOG_STATUS_TOKENS.active },
  { key: CATALOG_ITEM_STATUSES.RETIRED, label: 'Retired', token: CATALOG_STATUS_TOKENS.retired },
];

export const itemAliases = (item) => {
  if (!item) return [];
  if (Array.isArray(item.aliases)) return item.aliases;
  if (typeof item.aliases_or_source_labels === 'string') {
    return item.aliases_or_source_labels
      .split(';')
      .map((a) => a.trim())
      .filter(Boolean);
  }
  return [];
};

export const draftFromItem = (item) => ({
  code: item?.canonical_item_code || '',
  label: item?.canonical_item_label || item?.label || '',
  section: item?.section || '',
  subSection: item?.sub_section || '',
  valueKind: item?.default_value_kind || '',
  units: item?.default_units || '',
  aliases: itemAliases(item),
  signConvention: item?.normalization_or_sign_convention || item?.sign_convention || '',
  requiredLevel: item?.required_level || '',
  status: item?.status === CATALOG_ITEM_STATUSES.RETIRED ? CATALOG_ITEM_STATUSES.RETIRED : CATALOG_ITEM_STATUSES.ACTIVE,
});

const trimOrUndefined = (value) => {
  const text = String(value || '').trim();
  return text ? text : undefined;
};

// The POST body: every required field, the optional ones only when set.
export const createBodyFrom = (draft) => {
  const body = {
    canonical_item_code: draft.code.trim(),
    label: draft.label.trim(),
    section: draft.section,
    default_value_kind: draft.valueKind.trim(),
    aliases: draft.aliases,
  };
  const subSection = trimOrUndefined(draft.subSection);
  const units = trimOrUndefined(draft.units);
  const signConvention = trimOrUndefined(draft.signConvention);
  const requiredLevel = trimOrUndefined(draft.requiredLevel);
  if (subSection) body.sub_section = subSection;
  if (units) body.default_units = units;
  if (signConvention) body.sign_convention = signConvention;
  if (requiredLevel) body.required_level = requiredLevel;
  return body;
};

const sameAliases = (a, b) => a.length === b.length && a.every((alias, i) => alias === b[i]);

// The PATCH body: only what changed against the item as loaded.
export const editBodyFrom = (draft, item) => {
  const original = draftFromItem(item);
  const body = {};
  if (draft.label.trim() !== original.label) body.label = draft.label.trim();
  if (draft.section !== original.section) body.section = draft.section;
  if (draft.subSection.trim() !== original.subSection) body.sub_section = draft.subSection.trim();
  if (draft.valueKind.trim() !== original.valueKind) body.default_value_kind = draft.valueKind.trim();
  if (draft.units.trim() !== original.units) body.default_units = draft.units.trim();
  if (!sameAliases(draft.aliases, original.aliases)) body.aliases = draft.aliases;
  if (draft.signConvention.trim() !== original.signConvention) body.sign_convention = draft.signConvention.trim();
  if (draft.requiredLevel.trim() !== original.requiredLevel) body.required_level = draft.requiredLevel.trim();
  if (draft.status !== original.status) body.status = draft.status;
  return body;
};

export const CatalogItemForm = ({ mode = FORM_MODES.CREATE, item = null, onSaved, onCancel }) => {
  const dispatch = useDispatch();
  const { reload } = React.useContext(FmdcTenantContext);
  const { vocabularies } = useVocabularies();

  const isEdit = mode === FORM_MODES.EDIT;
  const [draft, setDraft] = React.useState(() => draftFromItem(item));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [touched, setTouched] = React.useState(false);

  const sectionOptions = vocabularyOptions(vocabularies, 'section');
  const valueKindOptions = vocabularyOptions(vocabularies, 'value_kind');
  const requiredLevelOptions = vocabularyOptions(vocabularies, 'required_level');

  // Whether the value kind is one the vocabulary lists; anything else is "Other…" plus text.
  const valueKindListed = valueKindOptions.some((o) => o.value === draft.valueKind);
  const [valueKindMode, setValueKindMode] = React.useState(() =>
    draft.valueKind && !valueKindOptions.some((o) => o.value === draft.valueKind) ? OTHER : draft.valueKind
  );
  React.useEffect(() => {
    // The vocabulary can arrive after the first render: once it does, settle whether the item's
    // kind is listed (so the select shows it) or custom (so the text field holds it).
    if (!vocabularies) return;
    if (draft.valueKind && !valueKindListed && valueKindMode !== OTHER) setValueKindMode(OTHER);
    if (draft.valueKind && valueKindListed && valueKindMode === OTHER) setValueKindMode(draft.valueKind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vocabularies]);

  const patch = (changes) => {
    setTouched(true);
    setDraft((prev) => ({ ...prev, ...changes }));
  };

  const codeInvalid = !isEdit && touched && !CATALOG_CODE_PATTERN.test(draft.code.trim());
  const labelMissing = touched && !draft.label.trim();
  const sectionMissing = touched && !draft.section;
  const valueKindMissing = touched && !draft.valueKind.trim();

  const isValid =
    (isEdit || CATALOG_CODE_PATTERN.test(draft.code.trim())) &&
    !!draft.label.trim() &&
    !!draft.section &&
    !!draft.valueKind.trim();

  const editBody = isEdit ? editBodyFrom(draft, item) : null;
  const nothingChanged = isEdit && Object.keys(editBody).length === 0;
  const canSubmit = isValid && !busy && !nothingChanged;

  const submit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const result = isEdit
      ? await updateCatalogItem(item.canonical_item_code, editBody)
      : await createCatalogItem(createBodyFrom(draft));
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    showSuccessBanner('Catalog item saved', null, dispatch);
    // A create or edit refreshes the catalog's aliases and embeddings in the background; the
    // tenant poll picks that up from here.
    reload();
    if (onSaved) onSaved(result.item);
  };

  const handleValueKindSelect = (value) => {
    setValueKindMode(value);
    if (value === OTHER) {
      patch({ valueKind: valueKindListed ? '' : draft.valueKind });
    } else {
      patch({ valueKind: value });
    }
  };

  const submitLabel = busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add item';

  return (
    <div>
      <FieldGrid>
        {isEdit ? (
          <TextField label="Code" value={draft.code} mono readOnly hint="The code cannot be changed." />
        ) : (
          <TextField
            label="Code"
            value={draft.code}
            onChange={(code) => patch({ code })}
            mono
            invalid={codeInvalid}
            hint="snake_case, starting with a letter"
          />
        )}
        <TextField
          label="Label"
          value={draft.label}
          onChange={(label) => patch({ label })}
          invalid={labelMissing}
          hint={labelMissing ? 'A label is required.' : 'Shown wherever the item is named.'}
        />
        <Select
          label="Section"
          value={draft.section}
          onChange={(section) => patch({ section })}
          options={sectionOptions}
          placeholder={sectionOptions.length ? 'Pick a section' : 'Loading sections…'}
          invalid={sectionMissing}
          hint={sectionMissing ? 'A section is required.' : undefined}
        />
        <TextField label="Sub-section" value={draft.subSection} onChange={(subSection) => patch({ subSection })} />
        <div>
          <Select
            label="Default value kind"
            value={valueKindMode}
            onChange={handleValueKindSelect}
            options={[...valueKindOptions, { value: OTHER, label: 'Other…' }]}
            placeholder="Pick a value kind"
            invalid={valueKindMissing}
            hint={valueKindMissing ? 'A value kind is required.' : undefined}
          />
          {valueKindMode === OTHER && (
            <Container top={8}>
              <TextField
                label="Custom value kind"
                value={draft.valueKind}
                onChange={(valueKind) => patch({ valueKind })}
                mono
                hint="A combination the vocabulary does not list."
              />
            </Container>
          )}
        </div>
        <TextField label="Default units" value={draft.units} onChange={(units) => patch({ units })} />
        <TextField
          label="Sign convention"
          value={draft.signConvention}
          onChange={(signConvention) => patch({ signConvention })}
        />
        {requiredLevelOptions.length > 0 ? (
          <Select
            label="Required level"
            value={draft.requiredLevel}
            onChange={(requiredLevel) => patch({ requiredLevel })}
            options={requiredLevelOptions}
            placeholder="Not set"
          />
        ) : (
          <TextField
            label="Required level"
            value={draft.requiredLevel}
            onChange={(requiredLevel) => patch({ requiredLevel })}
          />
        )}
      </FieldGrid>

      <Container top={16}>
        <AliasEditor aliases={draft.aliases} onChange={(aliases) => patch({ aliases })} />
      </Container>

      {isEdit && (
        <Container top={16}>
          {/* Labelled the way the TextFields above are (Body7, sentence case), so the form reads
              as one hierarchy; the control keeps "Status" as its accessible name. */}
          <Body7>Status</Body7>
          <Container top={4}>
            <SegmentedControl
              label="Status"
              options={STATUS_SEGMENTS}
              active={draft.status}
              onSelect={(status) => patch({ status })}
              withDots
              segmentWidth={96}
              hideLabel
            />
          </Container>
          <Container top={6}>
            <Body7>A retired item stops matching on the next run.</Body7>
          </Container>
        </Container>
      )}

      {error && (
        <Container top={16}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}

      {isEdit ? (
        // The drawer's inline save row: Cancel (when offered) before Save, hint after.
        <Container top={24} row verticallyCenter gap={12}>
          {onCancel && (
            <TransparentFlatButton onClick={() => !busy && onCancel()} disabled={busy}>
              Cancel
            </TransparentFlatButton>
          )}
          <FlatButton onClick={submit} disabled={!canSubmit}>
            {submitLabel}
          </FlatButton>
          {nothingChanged && touched && <Body7 isLowContrast>Nothing has changed.</Body7>}
        </Container>
      ) : (
        // The dialog's action row: right-aligned, Cancel before the primary.
        <Container top={24}>
          <DialogButtonRow>
            {onCancel && (
              <TransparentFlatButton onClick={() => !busy && onCancel()} disabled={busy}>
                Cancel
              </TransparentFlatButton>
            )}
            <FlatButton onClick={submit} disabled={!canSubmit}>
              {submitLabel}
            </FlatButton>
          </DialogButtonRow>
        </Container>
      )}
    </div>
  );
};
